import { Button } from "@/components/shadcn/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { IS_DEVELOPMENT } from "@/constants/env";
import { useVendorContext } from "@/contexts/vendor-context";
import useUniversalRouter from "@/hooks/use-universal-router";
import { FileText } from "lucide-react";
import {
  ComponentType,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type PortalComponentMap = Map<ComponentType<any>, string>;

const loadPortalComponentMap = async (): Promise<PortalComponentMap> => {
  const modules = await Promise.all([
    import("@/components/portal/product/Product"),
    import("@/components/portal/sidebar/Title"),
    import("@/components/portal/page/TitleBar"),
    import("@/components/portal/sidebar/InfoList"),
    import("@/components/portal/DataInspector"),
    import("@/components/portal/graph/GraphLoadingWrapper"),
    import("@/components/portal/product/ProductTypeBadge"),
    import("@/components/portal/IDBadge"),
    import("@/components/portal/product/purchase-option/PurchaseTypeBadge"),
    import("@/components/portal/product/purchase-option/Reference"),
    import("@/components/portal/ImageProxy"),
    import("@/components/portal/product/purchase-option/PurchaseOption"),
    import("@/components/portal/StatusBadge"),
    import("@/components/portal/contract/Bills"),
    import("@/components/portal/page/SectionTitleBar"),
    import("@/components/portal/contract/Entitlement"),
    import("@/components/portal/ErrorCode"),
    import("@/components/portal/user/User"),
    import("@/components/portal/EntitySelector"),
  ]);

  const paths = [
    "product/Product",
    "sidebar/Title",
    "page/TitleBar",
    "sidebar/InfoList",
    "DataInspector",
    "graph/GraphLoadingWrapper",
    "product/ProductTypeBadge",
    "IDBadge",
    "product/purchase-option/PurchaseTypeBadge",
    "product/purchase-option/Reference",
    "ImageProxy",
    "product/purchase-option/PurchaseOption",
    "StatusBadge",
    "contract/Bills",
    "page/SectionTitleBar",
    "contract/Entitlement",
    "ErrorCode",
    "user/User",
    "EntitySelector",
  ];

  return new Map(modules.map((mod, i) => [mod.default, paths[i]]));
};

const markComponentsFromPortal = (refMap: PortalComponentMap) => {
  const allElements = document.querySelectorAll("*");
  let markedCount = 0;

  allElements.forEach((element) => {
    const keys = Object.keys(element);
    const fiberKey = keys.find((key) => key.startsWith("__reactFiber"));

    if (!fiberKey) return;

    const fiber = (element as any)[fiberKey];
    if (!fiber) return;

    let currentFiber = fiber;

    while (currentFiber) {
      if (currentFiber.type) {
        const path = refMap.get(currentFiber.type);

        if (path) {
          (element as HTMLElement).setAttribute(
            "data-portal-component",
            "true",
          );
          (element as HTMLElement).setAttribute(
            "data-component-name",
            currentFiber.type.name,
          );
          (element as HTMLElement).setAttribute("data-component-path", path);
          markedCount++;
          break;
        }
      }

      currentFiber = currentFiber.return;
    }
  });

  console.info(`Marked ${markedCount} portal component elements`);
};

type PortalComponentContextType = {
  isHighlighting: boolean;
  toggleHighlight: () => void;
  setIsHighlighting: (value: boolean) => void;
  hasComponents: boolean;
};

const PortalComponentContext = createContext<PortalComponentContextType>({
  isHighlighting: false,
  toggleHighlight: () => {},
  setIsHighlighting: () => {},
  hasComponents: false,
});

export const usePortalComponent = () => useContext(PortalComponentContext);

const PortalComponentButtons = ({ refMap }: { refMap: PortalComponentMap }) => {
  const router = useUniversalRouter();
  const { selectedVendor } = useVendorContext();
  const { setIsHighlighting } = usePortalComponent();
  const [buttons, setButtons] = useState<
    Array<{ top: number; left: number; name: string; path: string }>
  >([]);

  useEffect(() => {
    const updateButtons = () => {
      const elements = document.querySelectorAll(
        "[data-portal-component][data-component-name][data-component-path]",
      );

      const rootElements = Array.from(elements).filter((el) => {
        const componentName = el.getAttribute("data-component-name");
        const parent = el.parentElement;
        if (!parent) return true;
        return parent.getAttribute("data-component-name") !== componentName;
      });

      setButtons(
        rootElements.map((el) => {
          const rect = el.getBoundingClientRect();
          return {
            top: rect.top + window.scrollY + 4,
            left: rect.right + window.scrollX - 32,
            name: el.getAttribute("data-component-name") || "",
            path: el.getAttribute("data-component-path") || "",
          };
        }),
      );
    };

    updateButtons();
    window.addEventListener("scroll", updateButtons);
    window.addEventListener("resize", updateButtons);
    document.addEventListener("portal-components-marked", updateButtons);

    return () => {
      window.removeEventListener("scroll", updateButtons);
      window.removeEventListener("resize", updateButtons);
      document.removeEventListener("portal-components-marked", updateButtons);
    };
  }, []);

  const handleClick = (componentPath: string) => {
    const currentPath = router.asPath;
    setIsHighlighting(false);
    if (selectedVendor) {
      router.push(
        `/${selectedVendor}/playground/sesamy-components/${componentPath}?from=${encodeURIComponent(currentPath)}`,
      );
    }
  };

  return (
    <TooltipProvider>
      {buttons.map(({ top, left, name, path }, index) => (
        <Tooltip key={index}>
          <TooltipTrigger asChild>
            <Button
              onClick={() => handleClick(path)}
              className="fixed z-50 h-7 w-7 bg-[rgb(234,179,8)] p-0 text-black hover:bg-[rgb(202,138,4)]"
              style={{ top: `${top}px`, left: `${left}px` }}
            >
              <FileText className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{name}.tsx</TooltipContent>
        </Tooltip>
      ))}
    </TooltipProvider>
  );
};

type PortalComponentWrapperProps = {
  children: ReactNode;
};

const PortalComponentWrapper = ({ children }: PortalComponentWrapperProps) => {
  const [isHighlighting, setIsHighlighting] = useState(false);
  const [hasComponents, setHasComponents] = useState(false);
  const [isRefMapReady, setIsRefMapReady] = useState(false);
  const refMapRef = useRef<PortalComponentMap | null>(null);

  const toggleHighlight = () => {
    if (!IS_DEVELOPMENT) return;
    setIsHighlighting((prev) => !prev);
  };

  // Load ref map lazily once on mount
  useEffect(() => {
    if (!IS_DEVELOPMENT) return;

    let mounted = true;
    let observer: MutationObserver | null = null;

    loadPortalComponentMap()
      .then((refMap) => {
        if (!mounted) return;
        refMapRef.current = refMap;
        setIsRefMapReady(true);

        const checkForComponents = () => {
          if (!mounted) return;
          const allElements = document.querySelectorAll("*");
          for (const element of allElements) {
            const fiberKey = Object.keys(element).find((k) =>
              k.startsWith("__reactFiber"),
            );
            if (!fiberKey) continue;
            let fiber = (element as any)[fiberKey];
            while (fiber) {
              if (fiber.type && refMap.has(fiber.type)) {
                setHasComponents(true);
                return;
              }
              fiber = fiber.return;
            }
          }
          setHasComponents(false);
        };

        checkForComponents();

        observer = new MutationObserver(checkForComponents);
        observer.observe(document.body, { childList: true, subtree: true });
      })
      .catch((err) => {
        console.info(
          "[PortalComponentWrapper] Failed to load component map:",
          err,
        );
        if (!mounted) return;
        refMapRef.current = null;
        setIsRefMapReady(false);
        setHasComponents(false);
      });

    return () => {
      mounted = false;
      setIsRefMapReady(false);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!IS_DEVELOPMENT || !isRefMapReady) return;

    if (isHighlighting) {
      document.body.setAttribute("data-highlight-portal", "true");

      const runMarking = () => {
        if (!refMapRef.current) return;
        markComponentsFromPortal(refMapRef.current);
        document.dispatchEvent(new CustomEvent("portal-components-marked"));
      };

      runMarking();
      const timer1 = setTimeout(runMarking, 100);
      const timer2 = setTimeout(runMarking, 500);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    } else {
      document.body.removeAttribute("data-highlight-portal");
      document.querySelectorAll("[data-portal-component]").forEach((el) => {
        el.removeAttribute("data-portal-component");
        el.removeAttribute("data-component-name");
        el.removeAttribute("data-component-path");
      });
    }
  }, [isHighlighting, isRefMapReady]);

  return (
    <PortalComponentContext.Provider
      value={{
        isHighlighting,
        toggleHighlight,
        setIsHighlighting,
        hasComponents,
      }}
    >
      {children}
      {IS_DEVELOPMENT && isHighlighting && refMapRef.current && (
        <PortalComponentButtons refMap={refMapRef.current} />
      )}
    </PortalComponentContext.Provider>
  );
};

export default PortalComponentWrapper;
